import React from 'react'
import {
  ExternalLink,
  Users,
  Boxes,
  ClipboardList,
  CheckSquare,
  MessageSquare,
  FileText,
  Search,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { SearchResult } from '@/types/search'
import { cn } from '@/lib/utils/cn'

interface DropdownResultsProps {
  results: SearchResult[]
  query?: string
  loading?: boolean
  selectedIndex?: number
  showViewAllButton?: boolean
  onResultClick: (result: SearchResult) => void
  onViewAllClick?: () => void
  resultItemRefs?: React.MutableRefObject<(HTMLDivElement | null)[]>
  viewAllButtonRef?: React.RefObject<HTMLButtonElement>
  label?: string
  labelAction?: React.ReactNode
  mobile?: boolean
}

export function DropdownResults({
  results,
  query = '',
  loading = false,
  selectedIndex = -1,
  showViewAllButton = true,
  onResultClick,
  onViewAllClick,
  resultItemRefs,
  viewAllButtonRef,
  label,
  labelAction,
  mobile = false,
}: DropdownResultsProps) {
  const getResultIcon = (type: SearchResult['type']) => {
    switch (type) {
      case 'user':
        return <Users className="h-4 w-4" />
      case 'workspace':
        return <Boxes className="h-4 w-4" />
      case 'project':
        return <ClipboardList className="h-4 w-4" />
      case 'task':
        return <CheckSquare className="h-4 w-4" />
      case 'message':
        return <MessageSquare className="h-4 w-4" />
      case 'document':
        return <FileText className="h-4 w-4" />
      default:
        return <Search className="h-4 w-4" />
    }
  }

  const getResultTypeLabel = (type: SearchResult['type']) => {
    switch (type) {
      case 'user':
        return 'User'
      case 'workspace':
        return 'Workspace'
      case 'project':
        return 'Project'
      case 'task':
        return 'Task'
      case 'message':
        return 'Message'
      case 'document':
        return 'Document'
      default:
        return 'Result'
    }
  }

  return (
    <div
      className={cn(
        mobile
          ? 'w-full bg-popover border rounded-md p-1 shadow-md'
          : 'absolute top-full left-0 right-0 z-50 mt-1 max-h-96 overflow-auto rounded-md border bg-popover p-1 shadow-md'
      )}
    >
      {label && results.length > 0 && (
        <div className="flex items-center justify-between px-2 py-1 border-b mb-1">
          <span className="text-xs font-medium text-muted-foreground">{label}</span>
          {labelAction && <div className="flex-shrink-0">{labelAction}</div>}
        </div>
      )}
      {results.length > 0 ? (
        <>
          {results.map((result, index) => (
            <div
              key={result.id}
              ref={resultItemRefs ? (el) => (resultItemRefs.current[index] = el) : undefined}
              className={cn(
                'flex items-center gap-3 rounded-sm px-2 py-2 text-sm cursor-pointer hover:bg-accent hover:text-accent-foreground',
                selectedIndex === index && 'bg-accent text-accent-foreground'
              )}
              onClick={() => onResultClick(result)}
            >
              {getResultIcon(result.type)}
              <div className="flex-1 min-w-0">
                <div className="font-medium truncate">{result.title}</div>
                {result.subtitle && (
                  <div className="text-xs text-muted-foreground truncate">{result.subtitle}</div>
                )}
              </div>
              <div className="text-xs text-muted-foreground">{getResultTypeLabel(result.type)}</div>
            </div>
          ))}

          {showViewAllButton && query.trim() && (
            <>
              <div className="border-t my-1" />
              <Button
                ref={viewAllButtonRef}
                variant="ghost"
                className={cn(
                  'w-full justify-start h-8 px-2 text-sm',
                  selectedIndex === results.length && 'bg-accent text-accent-foreground'
                )}
                onClick={onViewAllClick}
              >
                <ExternalLink className="mr-2 h-3 w-3" />
                View all results for "{query}"
              </Button>
            </>
          )}
        </>
      ) : query.trim() && !loading ? (
        <div className="px-2 py-6 text-center text-sm text-muted-foreground">
          No results found for "{query}"
        </div>
      ) : null}
    </div>
  )
}
